import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, MapPin, AlertTriangle, CheckCircle2, Printer, Loader2, ArrowLeft, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PreLabelAddressReview — modal that opens after a merchant picks a rate,
 * BEFORE the label is generated. Merchant can review and edit both addresses.
 *
 * Props:
 *   open              show/hide
 *   onClose()         close without doing anything
 *   shipmentId        id of the shipment to patch (can be null if we're only editing local form)
 *   token
 *   rate              the picked rate object {sell_rate, service, gateway_number, ...}
 *   initialFrom       {name, company, street1, city, state, zip, ...}
 *   initialTo         same shape
 *   onConfirm(edited) called when merchant clicks "Print label". Receives {from, to, requote_required}
 */
export default function PreLabelAddressReview({
  open, onClose, shipmentId, token, rate,
  initialFrom = {}, initialTo = {}, onConfirm,
}) {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verFrom, setVerFrom] = useState(null);   // {valid, corrected?, errors?}
  const [verTo, setVerTo] = useState(null);

  useEffect(() => {
    if (open) {
      setFrom(initialFrom);
      setTo(initialTo);
      setWarning(null);
      setVerFrom(null); setVerTo(null);
    }
  }, [open]); // eslint-disable-line

  const verifyBoth = async () => {
    setVerifying(true); setVerFrom(null); setVerTo(null);
    try {
      const call = (a) => fetch(`${API}/shipping/addresses/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(a),
      }).then((r) => r.json());
      const [f, t] = await Promise.all([call(from), call(to)]);
      setVerFrom(f); setVerTo(t);
      if (f.valid && t.valid && !f.corrected && !t.corrected) {
        toast.success('Both addresses verified — good to print.');
      } else if (f.corrected || t.corrected) {
        toast('Suggested corrections found. Review before printing.', { icon: '✏️' });
      }
    } catch (e) {
      toast.error('Verification failed. You can still print.');
    } finally { setVerifying(false); }
  };

  const applyCorrection = (side) => {
    const v = side === 'from' ? verFrom : verTo;
    if (!v?.corrected) return;
    const setter = side === 'from' ? setFrom : setTo;
    const current = side === 'from' ? from : to;
    setter({ ...current, ...v.corrected });
    toast.success(`${side === 'from' ? 'Origin' : 'Destination'} corrected.`);
    if (side === 'from') setVerFrom({ ...verFrom, corrected: null, applied: true });
    else setVerTo({ ...verTo, corrected: null, applied: true });
  };

  if (!open) return null;

  const fieldFrom = (k, ph, cls = '') =>
    <input value={from[k] || ''} onChange={(e) => setFrom({ ...from, [k]: e.target.value })} placeholder={ph}
      className={`w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm ${cls}`}
      data-testid={`review-from-${k}`} />;
  const fieldTo = (k, ph, cls = '') =>
    <input value={to[k] || ''} onChange={(e) => setTo({ ...to, [k]: e.target.value })} placeholder={ph}
      className={`w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm ${cls}`}
      data-testid={`review-to-${k}`} />;

  const zipChanged = (from.zip !== initialFrom.zip) || (to.zip !== initialTo.zip);

  const confirm = async () => {
    setSaving(true);
    try {
      let requoteRequired = false;
      if (shipmentId && shipmentId !== 'new' && shipmentId !== 'oneclick') {
        const body = {};
        ['name', 'company', 'street1', 'street2', 'city', 'state', 'zip', 'country', 'phone'].forEach((k) => {
          if (from[k] !== initialFrom[k]) body[`from_${k}`] = from[k];
          if (to[k] !== initialTo[k]) body[`to_${k}`] = to[k];
        });
        if (Object.keys(body).length > 0) {
          const r = await fetch(`${API}/shipping/shipments/${shipmentId}/addresses`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d.detail || 'Failed to save address changes');
          requoteRequired = !!d.requote_required;
          toast.success('Addresses saved.');
        }
      } else if (zipChanged) {
        requoteRequired = true;
      }

      if (requoteRequired) {
        setWarning('ZIP changed — please refetch rates before printing.');
        onConfirm && onConfirm({ from, to, requote_required: true });
      } else {
        onConfirm && onConfirm({ from, to, requote_required: false });
      }
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="prelabel-review-modal">
      <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-indigo-500" />
            <h2 className="text-lg font-semibold">Review addresses before printing</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="review-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-900 dark:text-indigo-200 flex-1 min-w-[240px]">
              You're about to print a <b>{rate?.service}</b> label · Gateway {rate?.gateway_number || '—'} · <b>${rate?.sell_rate?.toFixed(2)}</b>.
              You can still edit either address below — once the label prints, it's locked.
            </div>
            <Button variant="outline" size="sm" onClick={verifyBoth} disabled={verifying} data-testid="review-verify-btn">
              {verifying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
              Verify with carrier
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* From */}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-semibold">Ship from (Origin)</h3>
              <div className="space-y-2">
                {fieldFrom('name', 'Name')}
                {fieldFrom('company', 'Company (optional)')}
                {fieldFrom('street1', 'Street')}
                {fieldFrom('street2', 'Unit / Suite (optional)')}
                <div className="grid grid-cols-3 gap-2">
                  {fieldFrom('city', 'City')}
                  {fieldFrom('state', 'ST')}
                  {fieldFrom('zip', 'ZIP', from.zip !== initialFrom.zip ? 'border-amber-400' : '')}
                </div>
                {fieldFrom('phone', 'Phone')}
              </div>
              <VerificationCard side="from" v={verFrom} onApply={() => applyCorrection('from')} testid="from-verify" />
            </div>
            {/* To */}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-semibold">Ship to (Destination)</h3>
              <div className="space-y-2">
                {fieldTo('name', 'Recipient name')}
                {fieldTo('company', 'Company (optional)')}
                {fieldTo('street1', 'Street')}
                {fieldTo('street2', 'Unit / Suite (optional)')}
                <div className="grid grid-cols-3 gap-2">
                  {fieldTo('city', 'City')}
                  {fieldTo('state', 'ST')}
                  {fieldTo('zip', 'ZIP', to.zip !== initialTo.zip ? 'border-amber-400' : '')}
                </div>
                {fieldTo('phone', 'Phone (optional)')}
              </div>
              <VerificationCard side="to" v={verTo} onApply={() => applyCorrection('to')} testid="to-verify" />
            </div>
          </div>

          {zipChanged && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="zip-changed-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>A ZIP code changed. Rates depend on ZIP — we'll refetch the quote before printing.</span>
            </div>
          )}

          {warning && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800" data-testid="review-warning">{warning}</div>
          )}
        </div>

        <div className="flex justify-between items-center px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="review-back">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
          </Button>
          <div className="flex gap-2">
            <Button onClick={confirm} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-confirm">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : zipChanged ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Save &amp; Refetch</> : <><Printer className="w-3.5 h-3.5 mr-1.5" /> Confirm &amp; Print</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const VerificationCard = ({ side, v, onApply, testid }) => {
  if (!v) return null;
  if (v.applied) {
    return (
      <div className="mt-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800 flex items-center gap-1.5" data-testid={testid}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Correction applied.
      </div>
    );
  }
  if (v.unavailable) {
    // The check did not answer (rate limit, outage): nothing is known about the address.
    return (
      <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-1.5" data-testid={testid}>
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
        <span>Address check unavailable right now{(v.errors || [])[0]?.message ? `: ${v.errors[0].message}` : '.'} You can still print.</span>
      </div>
    );
  }
  if (v.valid && !v.corrected) {
    return (
      <div className="mt-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800 flex items-center gap-1.5" data-testid={testid}>
        <ShieldCheck className="w-3.5 h-3.5" /> Verified deliverable.
      </div>
    );
  }
  if (v.corrected) {
    return (
      <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid={testid}>
        <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Carrier suggests:</div>
        <div className="pl-4 space-y-0.5">
          <div>{v.corrected.street1}{v.corrected.street2 ? `, ${v.corrected.street2}` : ''}</div>
          <div>{v.corrected.city}, {v.corrected.state} {v.corrected.zip}</div>
        </div>
        <button type="button" onClick={onApply} className="mt-1.5 text-[11px] font-semibold text-amber-900 hover:underline" data-testid={`${testid}-apply`}>Apply correction →</button>
      </div>
    );
  }
  const err = (v.errors || [])[0]?.message || 'Not deliverable';
  return (
    <div className="mt-2 p-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-800 flex items-start gap-1.5" data-testid={testid}>
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> <span>{err}</span>
    </div>
  );
};
